import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Translate, withLocalize } from "react-localize-redux";
import CryptoJS from "crypto-js";
import {
    Paper,
    TextField,
    Typography,
    Box,
    Grid,
} from "@mui/material";
import { ButtonContinue } from "../../components/general/mini_components";
import { encryptData } from "../../utils/index.jsx";
import {
    selfChangePasswordService,
    evaluatePasswordComplexity,
    isPasswordComplexEnough,
} from "../../services/researcherSecurity";
import pathLogo from "../../img/logo_sherwood_web.png";

const Wrapper = styled(Paper)`
    padding: ${(props) => props.theme.spacing(6)};
    max-width: 480px;
    margin: 0 auto;
    margin-top: ${(props) => props.theme.spacing(6)};
    ${(props) => props.theme.breakpoints.up("md")} {
        padding: ${(props) => props.theme.spacing(10)};
    }
`;

/**
 * SHER-XYZ — forced password change page.
 *
 * Reached when the user logs in with an admin-set password (mustChangePassword=true on the JWT,
 * surfaced as `forceChangePassword=1` in localStorage by authService.js). The user types their
 * current (admin-set) password plus their chosen new password; we verify the old hash server-side,
 * re-wrap the user's existing rawKeyResearcher under the new plaintext password, swap the JWT,
 * and clear the flag.
 *
 * Investigation keys aren't re-wrapped here — they're encrypted with the rawKeyResearcher (which
 * doesn't change), so they keep working as-is once the new keyEncrypted unwraps to the same
 * rawKeyResearcher.
 */
function ChangePassword({ translate }) {
    const history = useHistory();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {
        if (e?.preventDefault) e.preventDefault();
        setErrorMessage(null);

        if (!oldPassword) {
            setErrorMessage(translate("change_password.error_old_required"));
            return;
        }
        if (!isPasswordComplexEnough(newPassword)) {
            setErrorMessage(translate("change_password.error_complexity"));
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage(translate("change_password.error_mismatch"));
            return;
        }
        if (oldPassword === newPassword) {
            setErrorMessage(translate("change_password.error_same_as_old"));
            return;
        }

        setSubmitting(true);
        try {
            const oldPasswordSha256 = CryptoJS.SHA256(oldPassword).toString(CryptoJS.enc.Base64);
            const newPasswordSha256 = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Base64);

            // Re-wrap the existing rawKeyResearcher with the new plaintext password.
            // Investigation keys stay valid because rawKeyResearcher itself is unchanged.
            const rawKeyResearcher = localStorage.getItem("rawKeyResearcher");
            if (!rawKeyResearcher) {
                throw new Error("Session missing rawKeyResearcher");
            }
            const newKeyEncrypted = encryptData(rawKeyResearcher, newPassword);

            const complexityProof = evaluatePasswordComplexity(newPassword);

            const response = await selfChangePasswordService({
                oldPasswordSha256,
                newPasswordSha256,
                newKeyEncrypted,
                complexityProof,
            });

            // Swap the JWT in localStorage so the rest of the app keeps working. The new
            // password is never stored (SHER-1147).
            if (response?.jwt) {
                localStorage.setItem("jwt", response.jwt);
            }
            localStorage.removeItem("forceChangePassword");

            history.push("/");
            // Hard reload to make sure no stale state with the old password lingers in memory.
            window.location.reload();
        } catch (err) {
            console.error("Self-change failed:", err);
            setErrorMessage(
                err?.errorMessage ||
                err?.response?.data?.errorMessage ||
                translate("change_password.error_generic"),
            );
            setSubmitting(false);
        }
    }

    const proof = evaluatePasswordComplexity(newPassword);

    return (
        <Wrapper>
            <Helmet title="Change Password" />
            <div style={{ textAlign: "center", paddingBottom: "1rem" }}>
                <img src={pathLogo} alt="Sherwood Science" height="55" />
            </div>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
                <Translate id="change_password.title" />
            </Typography>
            <Typography variant="body2" align="center" gutterBottom>
                <Translate id="change_password.subtitle" />
            </Typography>
            <form onSubmit={handleSubmit}>
                {/*
                 * SHER-218 — hidden username hint + explicit autocomplete attributes so the
                 * browser password manager treats this as a self-change form rather than
                 * popping its "Select which login to update" picker over the page (which is
                 * what CHOM admins were seeing when their browser had multiple saved accounts).
                 */}
                <input
                    type="text"
                    name="username"
                    autoComplete="username"
                    value={localStorage.getItem("email") || localStorage.getItem("uuid") || ""}
                    readOnly
                    hidden
                />
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            type="password"
                            fullWidth
                            label={translate("change_password.current")}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            data-testid="change-password-current"
                            autoFocus
                            inputProps={{ autoComplete: "current-password" }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type="password"
                            fullWidth
                            label={translate("change_password.new")}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            data-testid="change-password-new"
                            inputProps={{ autoComplete: "new-password" }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type="password"
                            fullWidth
                            label={translate("change_password.confirm")}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            data-testid="change-password-confirm"
                            inputProps={{ autoComplete: "new-password" }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box>
                            {[
                                { ok: proof.length >= 8, label: "change_password.req_length" },
                                { ok: proof.hasUpper, label: "change_password.req_upper" },
                                { ok: proof.hasNumber, label: "change_password.req_number" },
                                { ok: proof.hasSymbol, label: "change_password.req_symbol" },
                            ].map(({ ok, label }) => (
                                <Typography
                                    key={label}
                                    variant="body2"
                                    style={{ color: ok ? "green" : "#666" }}
                                >
                                    {ok ? "✓" : "○"} <Translate id={label} />
                                </Typography>
                            ))}
                        </Box>
                    </Grid>
                    {errorMessage && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="error">{errorMessage}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12} style={{ textAlign: "center" }}>
                        <ButtonContinue
                            type="submit"
                            disabled={submitting}
                            data-testid="change-password-submit"
                        >
                            <Translate id={submitting ? "general.loading.loading" : "change_password.submit"} />
                        </ButtonContinue>
                    </Grid>
                </Grid>
            </form>
        </Wrapper>
    );
}

export default withLocalize(ChangePassword);
